import { filterClasses } from "@swivl-mono/helpers";
import {
  ActivityActorType,
  MemberInterface,
  TicketActivity,
  TicketActivityType,
  TicketNote,
  formatTicketPriority,
  formatTicketStatus
} from "@swivl-mono/types";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { AiOutlineTag } from "react-icons/ai";
import { FiChevronsUp } from "react-icons/fi";
import { LuTicketCheck, LuUserRoundPen } from "react-icons/lu";
import { MdEditNote, MdOutlineCalendarMonth, MdOutlineLocationOn, MdRoute } from "react-icons/md";
import { RiRobot2Line } from "react-icons/ri";

import MembersModel from "../../../Models/Member/Members.Model";

type ActivityFeedProps = {
  activities: TicketActivity[];
  notes: TicketNote[];
  loading?: boolean;
  onAddReply?: (parentNoteId: number, body: string) => void;
};

function getActorName(activity: TicketActivity, membersMap: Record<number, MemberInterface>): string {
  // If we have a stored display name, use it as fallback
  const fallback = activity.actorDisplayName ?? "Someone";

  // For member actors, try to resolve from current member data
  if (activity.actorType === ActivityActorType.MEMBER && activity.actorId) {
    const memberId = parseInt(activity.actorId, 10);
    const member = membersMap[memberId];
    if (member) {
      if (member.account?.firstName || member.account?.lastName) {
        return `${member.account?.firstName ?? ""} ${member.account?.lastName ?? ""}`.trim();
      }
      return member.account?.email ?? fallback;
    }
  }

  // For system/integration/etc actors, use the stored display name
  return fallback;
}

function getMemberName(memberId: number, membersMap: Record<number, MemberInterface>): string {
  const member = membersMap[memberId];
  if (member) {
    if (member.account?.firstName || member.account?.lastName) {
      return `${member.account?.firstName ?? ""} ${member.account?.lastName ?? ""}`.trim();
    }
    return member.account?.email ?? `Member #${memberId}`;
  }
  return `Member #${memberId}`;
}

function formatMemberIds(value: unknown, membersMap: Record<number, MemberInterface>): string {
  if (!value) return "no one";

  // Handle array of member IDs
  if (Array.isArray(value)) {
    if (value.length === 0) return "no one";
    const names = value.map((id) => getMemberName(Number(id), membersMap));
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }

  // Handle comma-separated string of IDs
  if (typeof value === "string" && value.includes(",")) {
    const ids = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const names = ids.map((id) => getMemberName(Number(id), membersMap));
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }

  // Handle single ID
  if (typeof value === "number" || (typeof value === "string" && !isNaN(Number(value)))) {
    return getMemberName(Number(value), membersMap);
  }

  return String(value);
}

function getActivityDescription(activity: TicketActivity, membersMap: Record<number, MemberInterface>): string {
  const { type, payload } = activity;
  const actor = getActorName(activity, membersMap);

  switch (type) {
    case TicketActivityType.TICKET_CREATED:
      return `${actor} created this ticket.`;
    case TicketActivityType.STATUS_CHANGED:
      return `${actor} moved Status from ${formatTicketStatus(payload?.from)} to ${formatTicketStatus(payload?.to)}.`;
    case TicketActivityType.PRIORITY_CHANGED:
      return `${actor} changed Priority from ${formatTicketPriority(payload?.from)} to ${formatTicketPriority(payload?.to)}.`;
    case TicketActivityType.TITLE_CHANGED:
      return `${actor} updated the title.`;
    case TicketActivityType.DESCRIPTION_CHANGED:
      return `${actor} updated the description.`;
    case TicketActivityType.TAGS_CHANGED:
      return `${actor} updated tags.`;
    case TicketActivityType.ASSIGNEES_CHANGED: {
      const to = payload?.to;
      const isCleared = !to || (Array.isArray(to) && to.length === 0);
      if (isCleared) return `${actor} removed the assignee.`;
      const assignees = formatMemberIds(to, membersMap);
      return `${actor} assigned this ticket to ${assignees}.`;
    }
    case TicketActivityType.USER_CHANGED:
      return `${actor} changed the associated user.`;
    case TicketActivityType.DUE_DATE_CHANGED:
      return payload?.to ? `${actor} set due date to ${formatDueDate(payload.to)}.` : `${actor} cleared the due date.`;
    case TicketActivityType.LOCATION_CHANGED:
      return payload?.to ? `${actor} updated the location.` : `${actor} cleared the location.`;
    case TicketActivityType.NOTE_ADDED:
      return `${actor} added a note.`;
    case TicketActivityType.NOTE_EDITED:
      return `${actor} edited a note.`;
    case TicketActivityType.NOTE_DELETED:
      return `${actor} deleted a note.`;
    case TicketActivityType.TASK_ADDED:
      return `${actor} added a task.`;
    case TicketActivityType.TASK_DELETED:
      return `${actor} deleted a task.`;
    case TicketActivityType.TASK_EDITED:
      return `${actor} edited a task.`;
    case TicketActivityType.TASK_STATE_CHANGED:
      return payload?.to === "completed" ? `${actor} completed a task.` : `${actor} reopened a task.`;
    case TicketActivityType.TASK_ACTION_UPDATED:
      return `${actor} updated a task action.`;
    case TicketActivityType.TASKS_REORDERED:
      return `${actor} reordered tasks.`;
    case TicketActivityType.TICKET_ESCALATED:
      return "Bot escalated this ticket. Customer requested a human agent.";
    case TicketActivityType.TICKET_DISPATCHED:
      return payload?.to ? `Dispatched to ${payload.to} group.` : "Dispatched to a group.";
    case TicketActivityType.TICKET_CLAIMED:
      return `${actor} accepted this ticket.`;
    case TicketActivityType.BOT_UPDATED_TICKET:
      return "Bot updated this ticket with new context.";
    default:
      return `${actor} made a change.`;
  }
}

function formatDueDate(dateStr: string): string {
  return DateTime.fromISO(dateStr, { zone: "utc" }).toFormat("MM-dd-yyyy");
}

function formatTimestamp(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const time = d.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${month} ${day}, ${time}`;
}

function getActorInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function renderNoteBodyWithMentions(body: string): React.ReactNode {
  // Match @mentions like @John Doe or @username
  const mentionRegex = /@[\w\s]+/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(body)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(body.slice(lastIndex, match.index));
    }
    // Add the mention with highlight
    parts.push(
      <span key={match.index} className="font-medium text-orange-500">
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }

  return parts.length > 0 ? parts : body;
}

function getActivityIcon(type: TicketActivityType) {
  switch (type) {
    case TicketActivityType.TICKET_CREATED:
      return <LuTicketCheck size={16} />;
    case TicketActivityType.ASSIGNEES_CHANGED:
    case TicketActivityType.USER_CHANGED:
    case TicketActivityType.TASK_ADDED:
    case TicketActivityType.TASK_EDITED:
    case TicketActivityType.TASK_DELETED:
    case TicketActivityType.TASK_STATE_CHANGED:
    case TicketActivityType.TASK_ACTION_UPDATED:
    case TicketActivityType.TASKS_REORDERED:
      return <LuUserRoundPen size={16} />;
    case TicketActivityType.LOCATION_CHANGED:
      return <MdOutlineLocationOn size={16} />;
    case TicketActivityType.DUE_DATE_CHANGED:
      return <MdOutlineCalendarMonth size={16} />;
    case TicketActivityType.PRIORITY_CHANGED:
      return <FiChevronsUp size={16} />;
    case TicketActivityType.TAGS_CHANGED:
      return <AiOutlineTag size={16} />;
    case TicketActivityType.DESCRIPTION_CHANGED:
    case TicketActivityType.TITLE_CHANGED:
    case TicketActivityType.STATUS_CHANGED:
      return <MdEditNote size={16} />;
    case TicketActivityType.TICKET_ESCALATED:
    case TicketActivityType.BOT_UPDATED_TICKET:
      return <RiRobot2Line size={16} />;
    case TicketActivityType.TICKET_DISPATCHED:
      return <MdRoute size={16} />;
    case TicketActivityType.TICKET_CLAIMED:
      return <LuTicketCheck size={16} />;
    default:
      return null;
  }
}

function NoteCard({
  note,
  membersMap,
  onReply
}: {
  note: TicketNote;
  membersMap: Record<number, MemberInterface>;
  onReply?: (noteId: number, body: string) => void;
}) {
  const [replyText, setReplyText] = useState("");

  // Get actor name
  let actorName = note.actorDisplayName ?? "Someone";
  if (note.actorType === ActivityActorType.MEMBER && note.actorId) {
    const memberId = parseInt(note.actorId, 10);
    const member = membersMap[memberId];
    if (member) {
      if (member.account?.firstName || member.account?.lastName) {
        actorName = `${member.account?.firstName ?? ""} ${member.account?.lastName ?? ""}`.trim();
      } else if (member.account?.email) {
        actorName = member.account.email;
      }
    }
  }

  const initials = getActorInitials(actorName);

  const handleReply = () => {
    if (replyText.trim() && onReply) {
      onReply(note.id, replyText.trim());
      setReplyText("");
    }
  };

  return (
    <div className="rounded-lg border border-yellow-700 bg-yellow-50 p-3">
      {/* Header: Avatar, Name, Timestamp */}
      <div className="mb-2 flex items-center justify-between gap-2">
        {/* Avatar and Name */}
        <div className="flex items-center gap-1">
          <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-yellow-700 text-xs font-medium text-white">
            {initials}
          </div>
          <span className="body-sm-alt text-black">{actorName}</span>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-1">
          <span className="body-sm text-yellow-800">{formatTimestamp(note.createdAt)}</span>
          {note.edited && <span className="body-sm text-yellow-800">(edited)</span>}
        </div>
      </div>

      {/* Note body */}
      <div className="body-sm whitespace-pre-wrap text-[#333]">{renderNoteBodyWithMentions(note.body)}</div>

      {/* Reply input */}
      {onReply && (
        <div className="mt-3 flex items-center gap-2 rounded border border-[#E6D9A3] bg-white px-2 py-1.5">
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleReply()}
            placeholder="Leave a reply"
            className="flex-1 bg-transparent text-sm text-black outline-none placeholder:text-grey-600"
          />
          {replyText.trim() && (
            <button onClick={handleReply} className="flex h-5 w-5 items-center justify-center rounded bg-orange-500 text-white" type="button">
              <svg className="h-3 w-3" viewBox="0 0 14 14" fill="currentColor">
                <path
                  d="M1.75 7L12.25 7M12.25 7L7 1.75M12.25 7L7 12.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type FeedItem = { kind: "note"; date: Date; note: TicketNote } | { kind: "activity"; date: Date; activity: TicketActivity };

export function ActivityFeed({ activities, notes, loading, onAddReply }: ActivityFeedProps) {
  const membersMap = MembersModel.useState((s) => s.membersMap) ?? {};

  if (loading) {
    return <div className="py-4 text-center text-sm text-grey-600">Loading activity...</div>;
  }

  // Filter out NOTE_ADDED/NOTE_EDITED/NOTE_DELETED activities since we show actual notes
  const filteredActivities = activities.filter(
    (a) => a.type !== TicketActivityType.NOTE_ADDED && a.type !== TicketActivityType.NOTE_EDITED && a.type !== TicketActivityType.NOTE_DELETED
  );

  const hasContent = filteredActivities.length > 0 || notes.length > 0;

  if (!hasContent) {
    return <div className="py-4 text-center text-sm text-grey-600">No activity yet</div>;
  }

  // Merge notes and activities into a single list sorted newest-first
  const feedItems: FeedItem[] = [
    ...notes.map(
      (note): FeedItem => ({
        kind: "note",
        date: new Date(note.createdAt),
        note
      })
    ),
    ...filteredActivities.map(
      (activity): FeedItem => ({
        kind: "activity",
        date: new Date(activity.createdAt as unknown as string),
        activity
      })
    )
  ].sort((a, b) => b.date.getTime() - a.date.getTime());

  // Collect consecutive activity items into groups for connector lines
  return (
    <div className="space-y-3">
      {feedItems.map((item, index) => {
        if (item.kind === "note") {
          return <NoteCard key={`note-${item.note.id}`} note={item.note} membersMap={membersMap} onReply={onAddReply} />;
        }

        const { activity } = item;
        const icon = getActivityIcon(activity.type);
        const nextItem = feedItems[index + 1];
        const isLastInGroup = !nextItem || nextItem.kind !== "activity";

        return (
          <div key={`activity-${activity.id}`} className="flex gap-2">
            {/* Icon column with connector line */}
            <div className="flex flex-col items-center">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center text-grey-600">
                {icon ?? <div className="h-1.5 w-1.5 rounded-full bg-grey-600" />}
              </div>
              {!isLastInGroup && <div className="w-px flex-1 bg-grey-200" />}
            </div>

            {/* Content */}
            <div className={filterClasses("flex min-w-0 flex-1 items-start gap-2", !isLastInGroup && "pb-[14px]")}>
              {/* Description */}
              <p className="body-sm min-w-0 flex-1 text-grey-600">{getActivityDescription(activity, membersMap)}</p>

              {/* Timestamp */}
              <span className="body-sm shrink-0 whitespace-nowrap text-grey-600">{formatTimestamp(activity.createdAt as unknown as Date)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
